import type { StaffReq, RequirementTemplate, AuditEntry } from "~/types/requirement";

/**
 * API responses: columns use snake_case, relations keep camelCase
 * (Lucid's OnlySnakeCaseNamingStrategy only affects @column properties).
 * This normalizer ensures consistent camelCase throughout the app.
 */

interface RawStaffReq {
  id: string;
  staff_profile_id: string;
  template_id: string;
  is_recurring: boolean;
  status: "pending" | "submitted" | "verified" | "rejected";
  file_url: string | null;
  notes: string | null;
  rejection_reason: string | null;
  expiry_date: string | null;
  audit_trail: AuditEntry[];
  created_at: string;
  updated_at: string;
  staffProfile?: {
    id: string;
    position?: string;
    user?: {
      id: number;
      firstname: string;
      surname: string;
      avatar?: string;
    };
  };
  template?: RawRequirementTemplate;
}

interface RawRequirementTemplate {
  id: string;
  organisation_id: number;
  created_by: number;
  name: string;
  description: string | null;
  section: string;
  input_type: "field" | "note" | "document";
  rule: "mandatory" | "optional";
  type: "document" | "information";
  requires_file: boolean;
  requires_expiry_date: boolean;
  is_recurring: boolean;
  applies_to: "all_staff" | "hr_manager" | "manager" | "member" | "owner";
  created_at: string;
  updated_at: string;
  creator?: {
    id: number;
    firstname: string;
    surname: string;
    avatar?: string;
  };
  staffReq?: RawStaffReq[];
}

export function normalizeStaffRequir(raw: RawStaffReq): StaffReq {
  const result: StaffReq = {
    id: raw.id,
    staff_profile_id: raw.staff_profile_id,
    template_id: raw.template_id,
    is_recurring: raw.is_recurring,
    status: raw.status,
    file_url: raw.file_url,
    notes: raw.notes,
    expiry_date: raw.expiry_date,
    audit_trail: raw.audit_trail,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    rejection_reason: raw.rejection_reason,
  };

  if (raw.staffProfile) {
    result.staffProfile = {
      id: raw.staffProfile.id,
      full_name: raw.staffProfile.user
        ? `${raw.staffProfile.user.firstname} ${raw.staffProfile.user.surname}`
        : "Unknown",
      position: raw.staffProfile.position,
      user: raw.staffProfile.user,
    };
  }

  if (raw.template) {
    result.template = normalizeTemplate(raw.template);
  }

  return result;
}

export function normalizeTemplate(raw: RawRequirementTemplate): RequirementTemplate {
  return {
    id: raw.id,
    organisation_id: raw.organisation_id,
    created_by: raw.created_by,
    name: raw.name,
    description: raw.description,
    section: raw.section,
    input_type: raw.input_type,
    rule: raw.rule,
    requires_file: raw.requires_file,
    requires_expiry_date: raw.requires_expiry_date,
    is_recurring: raw.is_recurring,
    applies_to: raw.applies_to,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    creator: raw.creator,
    staffReq: raw.staffReq?.map(normalizeStaffRequir),
  };
}

export function normalizeStaffReqs(raw: RawStaffReq[]): StaffReq[] {
  return raw.map(normalizeStaffRequir);
}

export function normalizeTemplates(raw: RawRequirementTemplate[]): RequirementTemplate[] {
  return raw.map(normalizeTemplate);
}
